'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { IconCheck } from '@tabler/icons-react';
import * as React from 'react';

import { cn } from '@/lib/css-utils';

import { BaseUIComponentProps } from '../base';
import { Paragraph } from '../paragraph';

const Checkbox = React.forwardRef<
    React.ElementRef<typeof CheckboxPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
        hasError?: boolean;
        labelClassName?: string;
        labelParagraphClassName?: string;
    } & BaseUIComponentProps
>(({ className, labelClassName, labelParagraphClassName, children, mode, hasError, ...props }, ref) => {
    const node = (
        <CheckboxPrimitive.Root
            ref={ref}
            className={cn(
                'peer h-4 w-4 shrink-0 rounded-sm border focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
                'border-token-fill-icon-light-default hover:border-token-fill-icon-light-hover focus:border-token-fill-icon-light-focused',
                '[&>.indicator]:invisible data-[state=checked]:[&>.indicator]:visible',
                {
                    'border-token-fill-icon-light-error hover:border-token-fill-icon-light-error focus:border-token-fill-icon-light-error':
                        !!hasError,
                },
                className
            )}
            {...props}
        >
            <CheckboxPrimitive.Indicator
                className={cn('flex items-center justify-center text-current indicator')}
                forceMount
            >
                <IconCheck className="h-[14px] w-[14px]" />
            </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
    );

    if (children) {
        return (
            <label className={cn('inline-flex items-center justify-start gap-2', labelClassName)}>
                {node}
                <Paragraph
                    mode={mode}
                    fontWeight="regular"
                    size="xs"
                    className={cn(
                        'cursor-pointer select-none',
                        {
                            'text-token-fill-icon-light-error': !!hasError,
                        },
                        labelParagraphClassName
                    )}
                >
                    {children}
                </Paragraph>
            </label>
        );
    }

    return node;
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
